/* global theme */

if (!customElements.get('frequently-bought-together')) {
  class FrequentlyBoughtTogether extends HTMLElement {
    constructor() {
      super();
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleFbtVariantSelectChange = this.handleFbtVariantSelectChange.bind(this);
      this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
      this.handleInclusionIconClick = this.handleInclusionIconClick.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleSliderScroll = this.handleSliderScroll.bind(this);
      this.handleSliderPrev = this.handleSliderPrev.bind(this);
      this.handleSliderNext = this.handleSliderNext.bind(this);
      this.handleSliderResize = this.handleSliderResize.bind(this);
      this._suppressVariantSelectChange = false;
      this.currentSlideIndex = 0;
    }

    connectedCallback() {
      this.strings = JSON.parse(this.querySelector('.js-fbt-strings')?.textContent || '{}');
      this.totalEl = this.querySelector('.js-fbt-total');
      this.submitBtn = this.querySelector('.js-fbt-submit');
      this.errorEl = this.querySelector('.js-fbt-error');
      this.itemCountEl = this.querySelector('.js-fbt-item-count');
      this.sliderRow = this.querySelector('.fbt-bundle__row');
      this.sliderControls = this.querySelector('.js-fbt-slider-controls');
      this.sliderPrevBtn = this.querySelector('.js-fbt-slider-prev');
      this.sliderNextBtn = this.querySelector('.js-fbt-slider-next');
      this.sliderDots = this.querySelector('.js-fbt-slider-dots');
      this.itemRows = () => [...this.querySelectorAll('[data-fbt-item]')];

      this.querySelectorAll('.js-fbt-checkbox').forEach((input) => {
        input.addEventListener('change', this.handleCheckboxChange);
      });
      const mainInclusionIcon = this.querySelector('[data-fbt-item="main"] .js-fbt-inclusion-icon');
      if (mainInclusionIcon) {
        mainInclusionIcon.addEventListener('click', this.handleInclusionIconClick);
      }
      this.querySelectorAll('.js-fbt-variant-select').forEach((select) => {
        select.addEventListener('change', this.handleFbtVariantSelectChange);
      });
      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', this.handleSubmit);
      }

      document.addEventListener('on:variant:change', this.handleVariantChange);
      this.sliderRow?.addEventListener('scroll', this.handleSliderScroll, { passive: true });
      this.sliderPrevBtn?.addEventListener('click', this.handleSliderPrev);
      this.sliderNextBtn?.addEventListener('click', this.handleSliderNext);
      window.addEventListener('resize', this.handleSliderResize);
      this.handleCheckboxChange();
      this.updateSliderState();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.syncMainRowFromPdpInitial();
          if (!this.querySelector('[data-fbt-item="main"]')) {
            this.syncCompanionsFromInitialLead();
          }
        });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.handleVariantChange);
      this.sliderRow?.removeEventListener('scroll', this.handleSliderScroll);
      this.sliderPrevBtn?.removeEventListener('click', this.handleSliderPrev);
      this.sliderNextBtn?.removeEventListener('click', this.handleSliderNext);
      window.removeEventListener('resize', this.handleSliderResize);
      this.querySelectorAll('.js-fbt-variant-select').forEach((select) => {
        select.removeEventListener('change', this.handleFbtVariantSelectChange);
      });
      this.querySelector('[data-fbt-item="main"]')
        ?.querySelector('.js-fbt-inclusion-icon')
        ?.removeEventListener('click', this.handleInclusionIconClick);
    }

    /**
     * @param {Element} row
     * @returns {object[]}
     */
    parseRowVariants(row) {
      const el = row?.querySelector('.js-fbt-variants-json');
      if (!el) return [];
      try {
        return JSON.parse(el.textContent);
      } catch {
        return [];
      }
    }

    /**
     * @param {Element} row
     * @returns {string[]}
     */
    parseRowProductOptions(row) {
      const el = row?.querySelector('.js-fbt-product-options');
      if (!el) return [];
      try {
        const data = JSON.parse(el.textContent);
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    }

    /**
     * @param {string} name
     * @returns {string}
     */
    normalizeOptionKey(name) {
      const s = String(name || '')
        .toLowerCase()
        .trim();
      if (s === 'colour') return 'color';
      // Align PDP “finish / glaze” with companion “Color” so Fern Green matches across products.
      if (s === 'finish' || s === 'glaze' || s === 'colorway' || s === 'ceramic color') {
        return 'color';
      }
      return s;
    }

    /**
     * @param {object} variant
     * @param {string[]} optionNames
     * @returns {Record<string, string>}
     */
    buildOptionValueMap(variant, optionNames) {
      const map = {};
      if (!variant || !optionNames?.length) return map;
      for (let i = 0; i < optionNames.length; i += 1) {
        const key = this.normalizeOptionKey(optionNames[i]);
        const val = variant[`option${i + 1}`];
        if (key && val != null && String(val).length > 0) {
          map[key] = String(val).trim();
        }
      }
      return map;
    }

    /**
     * @param {string[]} companionOptionNames
     * @param {Record<string, string>} leadMap
     * @returns {Array<{key: string, idx: number}>}
     */
    getCompanionLeadPairs(companionOptionNames, leadMap) {
      if (!companionOptionNames?.length || !leadMap) return [];
      const keys = [
        ...new Set(
          companionOptionNames
            .map((n) => this.normalizeOptionKey(n))
            .filter((k) => k && leadMap[k] !== undefined)
        )
      ];
      return keys
        .map((key) => ({
          key,
          idx: companionOptionNames.findIndex((n) => this.normalizeOptionKey(n) === key)
        }))
        .filter((p) => p.idx >= 0)
        .sort((a, b) => a.idx - b.idx);
    }

    /**
     * @param {object} variant
     * @param {Array<{key: string, idx: number}>} pairs
     * @param {Record<string, string>} leadMap
     */
    variantMatchesPairs(variant, pairs, leadMap) {
      if (!variant || !pairs?.length) return false;
      return pairs.every((p) => {
        const want = leadMap[p.key];
        const got = variant[`option${p.idx + 1}`];
        if (want == null || got == null) return false;
        return String(got).trim() === String(want).trim();
      });
    }

    /**
     * @param {object[]} companionVariants
     * @param {Array<{key: string, idx: number}>} pairs
     * @param {Record<string, string>} leadMap
     * @returns {object|null}
     */
    pickMatchingCompanionVariant(companionVariants, pairs, leadMap) {
      if (!companionVariants?.length || !pairs?.length) return null;
      const candidates = companionVariants.filter((v) =>
        this.variantMatchesPairs(v, pairs, leadMap)
      );
      // Only in-stock matches: never fall back to another color/option when the lead’s selection is unavailable.
      return candidates.find((v) => v.available) || null;
    }

    /**
     * Companion has a Color (or Colour) option — pot size / volume-only products return false.
     * @param {string[]} companionOptionNames
     */
    companionHasColorOption(companionOptionNames) {
      return (
        Array.isArray(companionOptionNames) &&
        companionOptionNames.some((n) => this.normalizeOptionKey(n) === 'color')
      );
    }

    /**
     * Value the main product is using for color matching: named Color/Finish on the lead, or any
     * lead option value that exists on the companion’s color option (for odd admin labels).
     * @param {object} leadVariant
     * @param {string[]} leadOptionNames
     * @param {Set<string>} companionColorValues
     * @returns {string|null}
     */
    resolveLeadColorForCompanion(leadVariant, leadOptionNames, companionColorValues) {
      if (!leadVariant || !leadOptionNames?.length) return null;
      const leadMap = this.buildOptionValueMap(leadVariant, leadOptionNames);
      if (leadMap.color != null && String(leadMap.color).length > 0) {
        return String(leadMap.color).trim();
      }
      if (!companionColorValues?.size) return null;
      for (let i = 0; i < leadOptionNames.length; i += 1) {
        const val = leadVariant[`option${i + 1}`];
        if (val == null) continue;
        const s = String(val).trim();
        if (companionColorValues.has(s)) return s;
      }
      return null;
    }

    /**
     * First bundle card: main PDP product when included, otherwise first companion.
     * @returns {Element|null}
     */
    getLeadReferenceRow() {
      return this.querySelector('[data-fbt-item="main"]') || this.querySelector('[data-fbt-item="companion"]');
    }

    /**
     * @param {Element} row
     * @returns {object|null}
     */
    getVariantForRow(row) {
      const variants = this.parseRowVariants(row);
      const id = row?.dataset?.variantId;
      if (id == null || !variants.length) return null;
      return variants.find((v) => String(v.id) === String(id)) || null;
    }

    /**
     * @param {Element} row
     * @param {boolean} hidden
     */
    setFbtRowHidden(row, hidden) {
      if (!row || row.dataset?.fbtItem !== 'companion') return;
      row.classList.toggle('fbt-row--hidden', hidden);
      const cb = row.querySelector('.js-fbt-checkbox');
      if (hidden && cb) {
        cb.checked = false;
        cb.disabled = true;
      }
    }

    /** Hide “+” between items when a neighbor row is hidden. */
    normalizePlusSeparators() {
      const rowWrap = this.querySelector('.fbt-bundle__row');
      if (!rowWrap) return;
      const children = [...rowWrap.children];
      const visibleItems = children.filter(
        (el) => el.hasAttribute('data-fbt-item') && !el.classList.contains('fbt-row--hidden')
      );

      children.forEach((el) => {
        if (el.classList.contains('fbt-bundle__plus')) {
          el.classList.add('fbt-row--hidden');
        }
      });

      visibleItems.forEach((item, idx) => {
        if (idx === 0) return;
        let sibling = item.previousElementSibling;
        while (sibling && !sibling.classList.contains('fbt-bundle__plus')) {
          sibling = sibling.previousElementSibling;
        }
        if (sibling && sibling.classList.contains('fbt-bundle__plus')) {
          sibling.classList.remove('fbt-row--hidden');
        }
      });
      this.updateSliderState();
    }

    getVisibleSliderRows() {
      return this.itemRows().filter((row) => !row.classList.contains('fbt-row--hidden'));
    }

    isSliderEnabledForViewport() {
      return window.matchMedia('(max-width: 1279px)').matches;
    }

    getSliderCardsPerView() {
      if (window.matchMedia('(max-width: 768px)').matches) return 1;
      return 3;
    }

    getSliderPageCount() {
      const total = this.getVisibleSliderRows().length;
      const perView = this.getSliderCardsPerView();
      if (!total || !perView) return 0;
      return Math.ceil(total / perView);
    }

    renderSliderDots(pageCount, activeIndex) {
      if (!this.sliderDots) return;
      this.sliderDots.innerHTML = '';
      for (let i = 0; i < pageCount; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = `fbt-bundle__dot${i === activeIndex ? ' is-active' : ''}`;
        dot.setAttribute('aria-label', `Go to product group ${i + 1}`);
        dot.addEventListener('click', () => this.scrollToSlide(i));
        this.sliderDots.appendChild(dot);
      }
    }

    resolveCurrentSlideIndex() {
      if (!this.sliderRow) return 0;
      const maxIndex = Math.max(0, this.getSliderPageCount() - 1);
      const maxScrollLeft = Math.max(0, this.sliderRow.scrollWidth - this.sliderRow.clientWidth);
      if (this.sliderRow.scrollLeft >= maxScrollLeft - 2) {
        return maxIndex;
      }
      const width = this.sliderRow.clientWidth || 1;
      return Math.round(this.sliderRow.scrollLeft / width);
    }

    scrollToSlide(index) {
      if (!this.sliderRow) return;
      const clamped = Math.max(0, Math.min(index, Math.max(this.getSliderPageCount() - 1, 0)));
      const targetLeft = clamped * this.sliderRow.clientWidth;
      const maxScrollLeft = Math.max(0, this.sliderRow.scrollWidth - this.sliderRow.clientWidth);
      const left = Math.min(targetLeft, maxScrollLeft);
      this.sliderRow.scrollTo({ left, behavior: 'smooth' });
      this.currentSlideIndex = clamped;
      this.updateSliderNavState();
    }

    updateSliderNavState() {
      if (!this.sliderControls || !this.sliderRow) return;
      const pages = this.getSliderPageCount();
      const maxIndex = Math.max(0, pages - 1);
      this.currentSlideIndex = Math.max(0, Math.min(this.currentSlideIndex, maxIndex));
      if (this.sliderPrevBtn) this.sliderPrevBtn.disabled = this.currentSlideIndex <= 0;
      if (this.sliderNextBtn) this.sliderNextBtn.disabled = this.currentSlideIndex >= maxIndex;
      this.renderSliderDots(pages, this.currentSlideIndex);
    }

    updateSliderState() {
      if (!this.sliderRow || !this.sliderControls) return;
      const visibleRows = this.getVisibleSliderRows().length;
      const cardsPerView = this.getSliderCardsPerView();
      this.style.setProperty('--fbt-visible-cards', String(cardsPerView));
      const shouldSlide = this.isSliderEnabledForViewport() && visibleRows > cardsPerView;
      this.sliderRow.classList.toggle('is-slider-active', shouldSlide);
      this.sliderControls.classList.toggle('is-visible', shouldSlide);
      this.sliderControls.setAttribute('aria-hidden', shouldSlide ? 'false' : 'true');
      if (!shouldSlide) {
        this.currentSlideIndex = 0;
        this.sliderRow.scrollLeft = 0;
        if (this.sliderDots) this.sliderDots.innerHTML = '';
        if (this.sliderPrevBtn) this.sliderPrevBtn.disabled = true;
        if (this.sliderNextBtn) this.sliderNextBtn.disabled = true;
        return;
      }
      const maxIndex = Math.max(0, this.getSliderPageCount() - 1);
      this.currentSlideIndex = Math.min(this.currentSlideIndex, maxIndex);
      this.scrollToSlide(this.currentSlideIndex);
      this.updateSliderNavState();
    }

    handleSliderScroll() {
      if (!this.sliderRow?.classList.contains('is-slider-active')) return;
      this.currentSlideIndex = this.resolveCurrentSlideIndex();
      this.updateSliderNavState();
    }

    handleSliderPrev() {
      this.scrollToSlide(this.currentSlideIndex - 1);
    }

    handleSliderNext() {
      this.scrollToSlide(this.currentSlideIndex + 1);
    }

    handleSliderResize() {
      this.updateSliderState();
    }

    /**
     * When the lead variant changes: companions with a Color option are shown only if that color
     * exists in stock on the companion (e.g. Fern Green on saucers). Non-color companions
     * (pack qty, volume, etc.) stay visible and are not filtered by PDP color.
     */
    syncCompanionRowsFromLeadVariant(leadVariant, leadRow) {
      if (!leadVariant || !leadRow) return;

      const leadNames = this.parseRowProductOptions(leadRow);
      if (!leadNames.length) return;

      const leadMap = this.buildOptionValueMap(leadVariant, leadNames);

      this.itemRows().forEach((row) => {
        if (row === leadRow) return;

        const companionVariants = this.parseRowVariants(row);
        const companionNames = this.parseRowProductOptions(row);
        if (!companionVariants.length || !companionNames.length) return;

        if (!this.companionHasColorOption(companionNames)) {
          this.setFbtRowHidden(row, false);
          return;
        }

        const colorIdx = companionNames.findIndex((n) => this.normalizeOptionKey(n) === 'color');
        if (colorIdx < 0) return;

        const companionColorValues = new Set(
          companionVariants
            .map((v) => String(v[`option${colorIdx + 1}`] ?? '').trim())
            .filter(Boolean)
        );

        const leadColor = this.resolveLeadColorForCompanion(
          leadVariant,
          leadNames,
          companionColorValues
        );

        if (leadColor == null) {
          this.setFbtRowHidden(row, false);
          return;
        }

        const colorMatched = companionVariants.filter(
          (v) => String(v[`option${colorIdx + 1}`] ?? '').trim() === leadColor
        );
        const colorCandidates = colorMatched.filter((v) => v.available);
        if (!colorCandidates.length) {
          this.setFbtRowHidden(row, true);
          return;
        }

        this.setFbtRowHidden(row, false);

        const pairs = this.getCompanionLeadPairs(companionNames, leadMap);
        let picked = null;
        if (pairs.length) {
          picked =
            colorCandidates.find((v) => this.variantMatchesPairs(v, pairs, leadMap)) || null;
          if (!picked) {
            picked = this.pickMatchingCompanionVariant(colorCandidates, pairs, leadMap);
          }
        }
        if (!picked) picked = colorCandidates[0];

        this.syncVariantSelectValue(row, picked.id);
        this.applyVariantToRow(row, picked);
      });

      this.normalizePlusSeparators();
      this.handleCheckboxChange();
    }

    /**
     * When the bundle has no main row, align companions from the first companion’s initial variant.
     */
    syncCompanionsFromInitialLead() {
      const lead = this.getLeadReferenceRow();
      if (!lead || lead.dataset.fbtItem !== 'companion') return;
      if (!this.parseRowVariants(lead).length) return;
      const v = this.getVariantForRow(lead);
      if (v) this.syncCompanionRowsFromLeadVariant(v, lead);
    }

    /**
     * Hidden variant input on the main product form (current PDP selection).
     * @returns {number|null}
     */
    readPdpVariantInputId() {
      const input = document.querySelector('.js-product .js-product-form-main input[name="id"]');
      if (!input?.value) return null;
      const n = Number(input.value);
      return Number.isFinite(n) ? n : null;
    }

    /**
     * Prefer the requested variant when it is available to sell; otherwise first available
     * in the row (so the bundle line stays purchasable when the PDP selection is sold out).
     * @param {object[]} variants
     * @param {object|null|undefined} preferred
     * @returns {object|null}
     */
    resolveEffectiveVariant(variants, preferred) {
      if (!variants?.length) return preferred || null;
      if (preferred && preferred.available) return preferred;
      if (preferred && !preferred.available) {
        return variants.find((v) => v.available) || preferred;
      }
      return variants.find((v) => v.available) || variants[0] || null;
    }

    /**
     * @param {string} rawSrc
     * @param {number} width
     * @returns {string}
     */
    shopifyImageAtWidth(rawSrc, width) {
      if (!rawSrc || typeof rawSrc !== 'string') return '';
      let u = rawSrc.startsWith('//') ? `https:${rawSrc}` : rawSrc;
      const [path, qRaw = ''] = u.split('?');
      let q = qRaw.replace(/(^|&)width=\d+/gi, '').replace(/&+/g, '&').replace(/^&|&$/g, '');
      if (q) return `${path}?${q}&width=${width}`;
      return `${path}?width=${width}`;
    }

    /**
     * @param {string} rawSrc
     * @returns {object} Image URLs with src and srcset
     */
    buildFbtImageUrls(rawSrc) {
      if (!rawSrc || typeof rawSrc !== 'string') return { src: '', srcset: '' };
      const base = rawSrc.startsWith('//') ? `https:${rawSrc}` : rawSrc;
      if (/[?&]width=\d+/.test(base) || /_\d+x\.(jpg|jpeg|png|webp|gif)/i.test(base)) {
        return { src: base, srcset: '' };
      }
      const w200 = this.shopifyImageAtWidth(base, 200);
      const w400 = this.shopifyImageAtWidth(base, 400);
      const srcset = w200 === w400 ? '' : `${w200} 200w, ${w400} 400w`;
      return { src: w400 || base, srcset };
    }

    /**
     * Swaps the card image to match the variant (featured media or product fallback).
     * @param {Element} row
     * @param {object} variant
     */
    updateRowMedia(row, variant) {
      const img = row.querySelector('.js-fbt-item-img');
      if (!img || !variant) return;

      const fallback = row.dataset.fbtFallbackSrc || '';
      const featured = variant.featured_media;
      const previewSrc = featured?.preview_image?.src;
      const rawSrc = previewSrc || fallback;
      if (!rawSrc) return;

      const normalizedFallback =
        fallback && fallback.startsWith('//') ? `https:${fallback}` : fallback;

      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');

      if (!previewSrc && normalizedFallback) {
        img.src = normalizedFallback;
        img.removeAttribute('srcset');
        if (variant.title) img.alt = variant.title;
      } else {
        const { src, srcset } = this.buildFbtImageUrls(rawSrc);
        img.src = src || (rawSrc.startsWith('//') ? `https:${rawSrc}` : rawSrc);
        if (srcset) {
          img.srcset = srcset;
          if (!img.getAttribute('sizes')) img.setAttribute('sizes', '160px');
        } else {
          img.removeAttribute('srcset');
        }
      }

      const alt =
        featured && featured.alt != null && String(featured.alt).length > 0
          ? featured.alt
          : variant.title || '';
      if (alt) img.alt = alt;
    }

    /**
     * Align the "include current product" row with the PDP variant input after layout / other elements init.
     */
    syncMainRowFromPdpInitial() {
      const mainRow = this.querySelector('[data-fbt-item="main"]');
      if (!mainRow) return;

      const variants = this.parseRowVariants(mainRow);
      if (!variants.length) return;

      const pdpId = this.readPdpVariantInputId();
      const preferred =
        pdpId != null ? variants.find((v) => Number(v.id) === Number(pdpId)) : null;
      const effective = this.resolveEffectiveVariant(variants, preferred);
      if (!effective) return;

      this.applyVariantToRow(mainRow, effective);
      this.syncVariantSelectValue(mainRow, effective.id);
      this.syncCompanionRowsFromLeadVariant(effective, mainRow);
    }

    /**
     * @param {Element} row
     * @param {object} variant
     */
    applyVariantToRow(row, variant) {
      if (!row || !variant) return;

      row.dataset.variantId = String(variant.id);
      row.dataset.priceCents = String(variant.price);
      row.dataset.available = variant.available ? 'true' : 'false';

      const priceEl = row.querySelector('.js-fbt-item-price');
      if (priceEl && variant.price != null) {
        priceEl.textContent = this.formatMoney(Number(variant.price));
      }

      const priceWrap = row.querySelector('.js-fbt-price');
      if (priceWrap) {
        priceWrap.classList.toggle('price--sold-out', !variant.available);
      }

      const variantDisplay = row.querySelector('.js-fbt-variant-display');
      if (variantDisplay && variant.title) {
        variantDisplay.textContent = variant.title;
      }

      const cb = row.querySelector('.js-fbt-checkbox');
      if (cb) {
        if (!variant.available) {
          cb.checked = false;
          cb.disabled = true;
        } else {
          const wasDisabled = cb.disabled;
          cb.disabled = false;
          if (wasDisabled) {
            cb.checked = true;
          }
        }
      }

      this.updateRowMedia(row, variant);
    }

    /**
     * @param {Element} row
     * @param {string|number} variantId
     */
    syncVariantSelectValue(row, variantId) {
      const sel = row?.querySelector('.js-fbt-variant-select');
      if (!sel || variantId == null) return;
      const next = String(variantId);
      if (sel.value === next) return;
      this._suppressVariantSelectChange = true;
      sel.value = next;
      this._suppressVariantSelectChange = false;
    }

    handleFbtVariantSelectChange(evt) {
      if (this._suppressVariantSelectChange) return;
      const select = evt.target;
      if (!select?.classList?.contains('js-fbt-variant-select')) return;
      const row = select.closest('[data-fbt-item]');
      if (!row) return;

      const variants = this.parseRowVariants(row);
      const variant = variants.find((v) => String(v.id) === String(select.value));
      if (variant) {
        this.applyVariantToRow(row, variant);
      }

      const lead = this.getLeadReferenceRow();
      if (lead && row === lead && variant) {
        this.syncCompanionRowsFromLeadVariant(variant, lead);
      } else {
        this.handleCheckboxChange();
      }
    }

    getCartDrawer() {
      return document.querySelector('cart-drawer');
    }

    getSectionsParam() {
      const parts = ['cart-icon-bubble'];
      const cartDrawer = this.getCartDrawer();
      if (cartDrawer) {
        const sectionId = cartDrawer.closest('.shopify-section')?.id?.replace('shopify-section-', '');
        if (sectionId) parts.push(sectionId);
      }
      document.querySelectorAll('quick-order-list').forEach((qol) => {
        const sid = qol.closest('.shopify-section')?.id?.replace('shopify-section-', '');
        if (sid) parts.push(sid);
      });
      return [...new Set(parts)].join(',');
    }

    handleVariantChange(evt) {
      if (Number(evt.detail?.product?.id) !== Number(this.dataset.mainProductId)) return;

      const variant = evt.detail.variant;
      const mainRow = this.querySelector('[data-fbt-item="main"]');
      if (!mainRow || !variant) return;

      const variants = this.parseRowVariants(mainRow);
      const effective = this.resolveEffectiveVariant(variants, variant);
      if (!effective) return;

      this.applyVariantToRow(mainRow, effective);
      this.syncVariantSelectValue(mainRow, effective.id);

      this.syncCompanionRowsFromLeadVariant(effective, mainRow);
    }

    getSelectedLines() {
      return this.itemRows().filter((row) => {
        const cb = row.querySelector('.js-fbt-checkbox');
        return cb && cb.checked && row.dataset.available === 'true';
      });
    }

    sumSelectedCents() {
      return this.getSelectedLines().reduce((sum, row) => {
        const cents = parseInt(row.dataset.priceCents, 10);
        return sum + (Number.isFinite(cents) ? cents : 0);
      }, 0);
    }

    updateMainInclusionStatus() {
      const mainRow = this.querySelector('[data-fbt-item="main"]');
      if (!mainRow) return;

      const cb = mainRow.querySelector('.js-fbt-checkbox');
      const note = mainRow.querySelector('.js-fbt-inclusion-note');
      const messageEl = mainRow.querySelector('.js-fbt-inclusion-message');
      const hintEl = mainRow.querySelector('.js-fbt-inclusion-hint');
      if (!cb || !messageEl) return;

      const { strings } = this;
      const included = cb.checked && !cb.disabled;

      if (included) {
        messageEl.textContent =
          strings.included_in_bundle || 'This item is included in the bundle.';
        if (hintEl) {
          hintEl.textContent = strings.uncheck_to_remove || 'Uncheck to remove it.';
        }
        note?.classList.add('is-included');
        note?.classList.remove('is-excluded');
      } else {
        messageEl.textContent =
          strings.not_included_in_bundle || 'This item is not included in the bundle.';
        if (hintEl) {
          hintEl.textContent = strings.check_to_add || 'Check to add it.';
        }
        note?.classList.add('is-excluded');
        note?.classList.remove('is-included');
      }
    }

    handleInclusionIconClick(evt) {
      evt.preventDefault();
      evt.stopPropagation();

      const mainRow = evt.currentTarget.closest('[data-fbt-item="main"]');
      const cb = mainRow?.querySelector('.js-fbt-checkbox');
      if (!cb || cb.disabled) return;

      cb.checked = !cb.checked;
      cb.dispatchEvent(new Event('change', { bubbles: true }));
    }

    handleCheckboxChange() {
      const lines = this.getSelectedLines();
      const count = lines.length;
      const totalItems = this.getVisibleSliderRows().length;
      const cents = this.sumSelectedCents();

      if (this.totalEl) {
        this.totalEl.textContent = this.formatMoney(cents);
      }
      if (this.itemCountEl) {
        this.itemCountEl.textContent = `Items in bundle: ${count} of ${totalItems} selected`;
      }

      if (this.submitBtn) {
        const { strings } = this;
        let label = strings.add_selected || 'Add to cart';
        if (count === 0) {
          label = strings.add_selected || 'Add to cart';
        } else if (count === 1) {
          label = strings.add_one || 'Add to cart';
        } else if (count === 2) {
          label = strings.add_both || 'Add both to cart';
        } else {
          const addAllTemplate = strings.add_all || 'Add all items to cart';
          label = addAllTemplate.replace(/\{\{\s*count\s*\}\}/gi, String(count));
        }
        this.submitBtn.textContent = label;
        this.submitBtn.disabled = count === 0;
      }
      this.updateMainInclusionStatus();
      this.updateSliderNavState();
    }

    formatMoney(cents) {
      const amount = cents / 100;
      const code = this.dataset.currencyCode || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: code
        }).format(amount);
      } catch {
        return amount.toFixed(2);
      }
    }

    setError(msg) {
      if (!this.errorEl) return;
      if (msg) {
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      } else {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }

    async handleSubmit(evt) {
      evt.preventDefault();
      const lines = this.getSelectedLines();
      if (!lines.length) return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.setError('');
      this.submitBtn.classList.add('is-loading');
      this.submitBtn.setAttribute('aria-disabled', 'true');

      const items = lines.map((row) => ({
        id: Number(row.dataset.variantId),
        quantity: 1
      }));

      const body = {
        items,
        sections: this.getSectionsParam(),
        sections_url: window.location.pathname
      };

      try {
        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(body)
        });

        const data = await response.json();
        if (!response.ok) {
          const err =
            typeof data.description === 'string'
              ? data.description
              : data.message || theme.strings.cartError;
          throw new Error(err);
        }

        if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
          return;
        }

        if (data.sections && data.sections['cart-icon-bubble']) {
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble) {
            cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
          }
        }

        const cartDrawer = this.getCartDrawer();
        if (cartDrawer && data.sections) {
          const openDrawer =
            (theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches?.md) ||
            theme.settings.afterAtc === 'drawer';

          setTimeout(() => {
            cartDrawer.renderContents(data, openDrawer);
          }, 400);
        } else if (cartDrawer) {
          setTimeout(() => cartDrawer.refreshCartDrawer(), 400);
        }
      } catch (error) {
        this.setError(error.message || theme.strings.cartError);
        const cartDrawer = this.getCartDrawer();
        if (cartDrawer) cartDrawer.refreshCartDrawer();
      } finally {
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.removeAttribute('aria-disabled');
      }
    }
  }

  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
}
